import { useSession } from '@/app/session-store'
import { callNative } from '@/bridge'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')
const TIMEOUT_MS = 20_000

export class ApiError extends Error {
  readonly status: number
  readonly data: unknown
  constructor(status: number, data: unknown, message = `Request failed with ${status}`) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

let refreshing: Promise<string> | null = null

/** Token lives in native; dedupe concurrent 401s into one refresh call. */
function refreshAccessToken(): Promise<string> {
  refreshing ??= callNative('refreshToken')
    .then(({ accessToken }) => {
      useSession.getState().setAccessToken(accessToken)
      return accessToken
    })
    .finally(() => {
      refreshing = null
    })
  return refreshing
}

/** AbortSignal.any() is too new for older WKWebView; combine manually. */
function timeoutSignal(outer?: AbortSignal | null): { signal: AbortSignal; cleanup: () => void } {
  const ctrl = new AbortController()
  const t = setTimeout(
    () => ctrl.abort(new DOMException('Request timed out', 'TimeoutError')),
    TIMEOUT_MS,
  )
  const onAbort = () => ctrl.abort(outer?.reason)
  if (outer?.aborted) ctrl.abort(outer.reason)
  else outer?.addEventListener('abort', onAbort, { once: true })
  return {
    signal: ctrl.signal,
    cleanup: () => {
      clearTimeout(t)
      outer?.removeEventListener('abort', onAbort)
    },
  }
}

async function send(url: string, init: RequestInit, token: string | undefined): Promise<Response> {
  const headers = new Headers(init.headers)
  // Callers may bring their own auth (e.g. /settings admin login); don't override it.
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Passcode ${token}`)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  if (typeof init.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  const orgId = useSession.getState().session?.orgId
  if (orgId && !headers.has('Organization')) headers.set('Organization', orgId)

  const { signal, cleanup } = timeoutSignal(init.signal)
  try {
    return await fetch(url.startsWith('http') ? url : `${BASE_URL}${url}`, {
      ...init,
      headers,
      signal,
    })
  } finally {
    cleanup()
  }
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined
  const type = res.headers.get('Content-Type') ?? ''
  return type.includes('json') ? res.json() : res.text()
}

/**
 * Single HTTP entry point for the app; also the orval mutator.
 * Returns orval's fetch-client shape: { data, status, headers }.
 */
export async function apiFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  let res = await send(url, init, useSession.getState().session?.accessToken)

  // Native refresh only applies to the native session token, not caller-supplied auth.
  if (res.status === 401 && !new Headers(init.headers).has('Authorization')) {
    const fresh = await refreshAccessToken().catch(() => null)
    if (fresh) res = await send(url, init, fresh)
  }

  const data = await parseBody(res)
  if (!res.ok) throw new ApiError(res.status, data)
  return { data, status: res.status, headers: res.headers } as T
}

/** Convenience for hand-written calls that only need the body. */
export const api = {
  get: <T>(url: string, init?: RequestInit) =>
    apiFetch<{ data: T }>(url, { ...init, method: 'GET' }).then((r) => r.data),
  post: <T>(url: string, body?: unknown, init?: RequestInit) =>
    apiFetch<{ data: T }>(url, {
      ...init,
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    }).then((r) => r.data),
  patch: <T>(url: string, body?: unknown, init?: RequestInit) =>
    apiFetch<{ data: T }>(url, {
      ...init,
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    }).then((r) => r.data),
  put: <T>(url: string, body?: unknown, init?: RequestInit) =>
    apiFetch<{ data: T }>(url, {
      ...init,
      method: 'PUT',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    }).then((r) => r.data),
  delete: <T = void>(url: string, init?: RequestInit) =>
    apiFetch<{ data: T }>(url, { ...init, method: 'DELETE' }).then((r) => r.data),
}
